import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Alert,
  Spinner,
  Progress,
  ListGroup, // Tetap diimport jika digunakan untuk tampilan lain
} from 'reactstrap';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import moment from 'moment';
import 'moment/locale/id';

// --- BAGIAN INI SUDAH DIUPDATE UNTUK BAR CHART ---
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);
// --- AKHIR BAGIAN UPDATE Chart.js ---

// Import your services (Asumsi path ini benar)
import { tryoutGetSummarySkor, tryoutGetAnalisaSkor } from '../../../services/tryoutService';

moment.locale('id');

const TryOutResultDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [resultData, setResultData] = useState(null);
  const [analisaSkorData, setAnalisaSkorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const queryParams = new URLSearchParams(location.search);
  const resultId = queryParams.get('result_id');
  const idTryout = queryParams.get('id_tryout');
  const idJenisSeleksiFromUrl = queryParams.get('id_jenis_seleksi');
  const idTipeKontenFromUrl = queryParams.get('id_tipe_konten');
  const idPaketFromUrl = queryParams.get('id_paket');

  // --- Helper Functions ---
  const getBreadcrumbPaddingTop = () => {
    if (window.innerWidth <= 767.98) {
      return '70px';
    } else if (window.innerWidth <= 991.98) {
      return '80px';
    }
    return '100px';
  };

  const getStatusBadgeColor = (status) => {
    if (status === 'LULUS') {
      return 'bg-success-subtle text-success';
    } else if (status === 'TIDAK LULUS') {
      return 'bg-danger-subtle text-danger';
    }
    return 'bg-secondary-subtle text-secondary';
  };
  
  // Fungsi yang diubah: Menggunakan NAB dinamis (passingGradeMin)
  const getScoreBadgeColor = (score, nab) => {
    if (score >= nab) return 'bg-success-subtle text-success';
    return 'bg-danger-subtle text-danger';
  };

  const getCorrectnessColor = (correct, total) => {
    if (total === 0) return 'text-secondary';
    if (correct / total >= 0.8) return 'text-success';
    if (correct / total >= 0.5) return 'text-warning';
    return 'text-danger';
  };

  const formatDuration = (totalSeconds) => {
    if (totalSeconds === null || isNaN(totalSeconds) || totalSeconds < 0) {
      return '0 detik';
    }
    const seconds = Math.floor(totalSeconds);
    if (seconds < 60) {
      return `${seconds} detik`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (remainingSeconds === 0) {
        return `${minutes} menit`;
      } else {
        return `${minutes} menit ${remainingSeconds} detik`;
      }
    }
  };

  // --- Fetch Data Effect ---
  useEffect(() => {
    const fetchResultData = async () => {
      if (!resultId) {
        setError("ID Hasil Try Out tidak ditemukan di URL.");
        setLoading(false);
        return;
      }
      if (!idJenisSeleksiFromUrl) {
        setError("Jenis Seleksi tidak ditemukan di URL.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // 1. Fetch summary skor (untuk mendapatkan metadata seperti nama, durasi maksimal)
        const summaryResponse = await tryoutGetSummarySkor(resultId, idTipeKontenFromUrl, idJenisSeleksiFromUrl);
        if (!summaryResponse.success || !summaryResponse.data || summaryResponse.data.length === 0) {
          console.warn("Gagal memuat ringkasan hasil try out, menggunakan data default untuk metadata.");
        }
        const fetchedSummary = summaryResponse.data ? summaryResponse.data[0] : {};

        // 2. Fetch analysis skor (Logika Error Handling yang telah diperbaiki)
        const analisaResponse = await tryoutGetAnalisaSkor(resultId, idJenisSeleksiFromUrl, idTipeKontenFromUrl, idPaketFromUrl);
        
        // Cek Kegagalan Nyata (Success: false)
        if (!analisaResponse.success) {
            throw new Error(analisaResponse.message || 'Gagal memuat analisis per bagian try out.');
        }

        let fetchedAnalisa = [];
        if (Array.isArray(analisaResponse.data) && analisaResponse.data.length > 0) {
             // Mapping skor_per_kategori ke skor_anda untuk konsistensi di frontend
             fetchedAnalisa = analisaResponse.data.map(item => ({
                ...item,
                skor_anda: item.skor_per_kategori // Sesuaikan skor_per_kategori ke skor_anda
             }));
        } else {
             console.warn("Analisis skor berhasil dimuat, tetapi data analisis per kategori kosong.");
        }
        
        // =========================================================================
        // START: PERHITUNGAN TOTAL DARI ANALISA SKOR DATA
        // =========================================================================
        
        const totalSkorAnda = fetchedAnalisa.reduce((sum, item) => sum + (item.skor_per_kategori || 0), 0);
        const totalNab = fetchedAnalisa.reduce((sum, item) => sum + (item.passing_grade_min || 0), 0);
        const totalDurasiDetik = fetchedAnalisa.reduce((sum, item) => sum + (item.durasi_per_kategori_detik || 0), 0);
        
        const totalDurasiFormatted = formatDuration(totalDurasiDetik);
        const overallStatus = totalSkorAnda >= totalNab ? 'LULUS' : 'TIDAK LULUS';
        const totalDurasiMenit = Math.ceil(totalDurasiDetik / 60);

        // =========================================================================
        // END: PERHITUNGAN TOTAL DARI ANALISA SKOR DATA
        // =========================================================================


        setAnalisaSkorData(fetchedAnalisa); 

        const newResultData = {
          id_tryout_answer: resultId,
          id_tryout: idTryout,
          
          // Menggunakan hasil perhitungan dari analisa skor
          total_score: totalSkorAnda,
          status: overallStatus,
          passing_threshold_total: totalNab,
          time_taken_overall_display: totalDurasiFormatted,
          time_taken_overall_minutes: totalDurasiMenit,

          // Metadata dari fetchedSummary
          duration_allowed_minutes: fetchedSummary.maksimal_durasi_jawaban_menit || 100, // Default jika tidak ada data summary
          nama_tryout: fetchedSummary.nama_tryout || 'Try Out (Nama Tidak Diketahui)',
          created_at: fetchedSummary.created_at, 
        };

        setResultData(newResultData);

      } catch (err) {
        console.error("Error fetching tryout result:", err);
        setError(`Terjadi kesalahan saat memuat data: ${err.message || 'Mohon coba lagi.'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchResultData();
  }, [resultId, idTryout, idJenisSeleksiFromUrl, idTipeKontenFromUrl, idPaketFromUrl]);

  // --- Data for Bar Chart ---
  const barChartData = (analisaSkorData.length > 0 && resultData) ? {
    labels: analisaSkorData.map(item => item.kategori_soal),
    datasets: [
      {
        label: 'Skor Anda',
        data: analisaSkorData.map(item => item.skor_anda),
        backgroundColor: 'rgba(75, 192, 192, 0.8)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
      {
        label: 'Nilai Ambang Batas (NAB)',
        // Menggunakan passing_grade_min
        data: analisaSkorData.map(item => item.passing_grade_min || 0),
        backgroundColor: 'rgba(255, 99, 132, 0.8)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
      },
    ],
  } : null;

  // --- Conditional Rendering ---
  if (loading) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
        <Spinner color="primary" className="me-2" /> Memuat hasil Try Out...
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content" style={{ minHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
        <Container fluid style={{ paddingTop: getBreadcrumbPaddingTop() }}>
          <Alert color="danger" className="text-center mt-3">
            <i className="mdi mdi-alert-circle-outline me-2"></i> {error}
            <div className="mt-3">
              <Button color="primary" onClick={() => window.location.reload()}>Muat Ulang Halaman</Button>
            </div>
          </Alert>
        </Container>
      </div>
    );
  }

  if (!resultData) {
    return (
      <div className="page-content" style={{ minHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
        <Container fluid style={{ paddingTop: getBreadcrumbPaddingTop() }}>
          <Alert color="info" className="text-center mt-3">
            <i className="mdi mdi-information-outline me-2"></i> Data hasil Try Out tidak ditemukan.
          </Alert>
          <div className="text-center mt-3">
            <Button color="secondary" onClick={() => navigate(-1)}>Kembali</Button>
          </div>
        </Container>
      </div>
    );
  }

  // Calculate percentage of time used for overall summary
  const durationAllowed = resultData.duration_allowed_minutes || 1; // Mencegah pembagian nol
  const timeUsedPercentage = (resultData.time_taken_overall_minutes / durationAllowed) * 100;
  const timeStatusColor = timeUsedPercentage <= 90 ? 'success' : timeUsedPercentage <= 100 ? 'warning' : 'danger';


  return (
    <React.Fragment>
      {/* PERBAIKAN SCROLLING: Menambahkan inline style overflowY: 'auto' */}
      <div className="page-content" style={{ overflowY: 'auto' }}>
        <Container fluid>
          <Breadcrumbs
            title="Hasil Try Out"
            // breadcrumbItem={resultData.nama_tryout || 'Detail Hasil'}
          />

          <Row className="mb-3">
            <Col lg={12}>
              <Alert color="info" className="d-flex align-items-start border-0 rounded p-3 custom-alert-info-bg">
                <div className="flex-shrink-0 me-3 mt-1">
                  <i className="mdi mdi-information-outline font-size-20 text-info"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="alert-heading text-dark mb-2">Ringkasan Hasil Pengerjaan:</h6>
                  <p className="mb-0 font-size-13 text-dark">
                    Berikut adalah detail hasil pengerjaan **{resultData.nama_tryout || 'ini'}**
                  </p>
                </div>
              </Alert>
            </Col>
          </Row>

          {/* 1. Ringkasan Hasil Keseluruhan */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded">
                <CardBody className="p-3">
                  <h5 className="card-title mb-3 text-dark">Ringkasan Hasil Keseluruhan</h5>
                  <Row className="text-center gy-3">
                    <Col xs={12} md={4}>
                      <div className="p-3 bg-light rounded hover-shadow transition-300ms">
                        <p className="font-size-14 text-muted mb-1">Total Skor Anda</p>
                        <h3 className="fw-bold mb-0" style={{color: resultData.status === 'LULUS' ? '#28a745' : '#dc3545'}}>
                          {resultData.total_score}
                        </h3>
                        <span className={`badge font-size-12 px-2 py-1 rounded-pill ${getStatusBadgeColor(resultData.status)}`}>
                          {resultData.status}
                        </span>
                      </div>
                    </Col>
                    <Col xs={12} md={4}>
                      <div className="p-3 bg-light rounded hover-shadow transition-300ms">
                        <p className="font-size-14 text-muted mb-1">Nilai Ambang Batas (NAB) Minimal</p>
                        <h4 className="fw-semibold mb-0 text-primary">
                          {resultData.passing_threshold_total}
                        </h4>
                        <small className="text-muted">Untuk Lulus</small>
                      </div>
                    </Col>
                    <Col xs={12} md={4}>
                      <div className="p-3 bg-light rounded hover-shadow transition-300ms">
                        <p className="font-size-14 text-muted mb-1">Waktu Pengerjaan</p>
                        <h4 className="fw-semibold mb-0 text-info">
                          {resultData.time_taken_overall_display}
                        </h4>
                        <small className="text-muted">Dari {resultData.duration_allowed_minutes} Menit</small>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* 2. Analisis Per Bagian (Dinamis dari Analisa Skor) */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded">
                <CardBody className="p-3">
                  <h5 className="card-title mb-3 text-dark">Analisis Per Bagian</h5>
                  <Row className="gy-3 mb-4">
                    {analisaSkorData.length > 0 ? (
                      analisaSkorData.map((dataItem, index) => {
                        // Mengambil NAB dari kolom passing_grade_min
                        const currentNab = dataItem.passing_grade_min || 0;
                        const totalQuestions = (dataItem.jumlah_benar || 0) + (dataItem.jumlah_salah || 0) + (dataItem.jumlah_tidak_dijawab || 0);

                        return (
                          <Col xs={12} md={4} key={index}>
                            <Card className="shadow-sm h-100 border-0 custom-section-card">
                              <CardBody className="py-2 px-3">
                                <h6 className="mb-1 fw-bold text-dark">{dataItem.kategori_soal.toUpperCase()}</h6>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="font-size-14 text-muted">Skor Anda:</span>
                                  <span className={`fw-bold font-size-16 ${getScoreBadgeColor(dataItem.skor_anda, currentNab)} rounded-pill px-2 py-1`}>
                                    {dataItem.skor_anda}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="font-size-12 text-muted">NAB {dataItem.kategori_soal.toUpperCase()}:</span>
                                  <span className="fw-semibold font-size-12 text-primary">
                                    {currentNab}
                                  </span>
                                </div>
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between font-size-13 text-muted">
                                  <span>Benar: <span className={`fw-semibold ${getCorrectnessColor(dataItem.jumlah_benar, totalQuestions)}`}>{dataItem.jumlah_benar}</span></span>
                                  <span>Salah: <span className="text-danger fw-semibold">{dataItem.jumlah_salah}</span></span>
                                  <span>Tidak Dijawab: <span className="text-secondary fw-semibold">{dataItem.jumlah_tidak_dijawab}</span></span>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        );
                      })
                    ) : (
                      <Col xs={12} className="text-center text-muted">
                        <p>Tidak ada data analisis per bagian yang tersedia.</p>
                      </Col>
                    )}
                  </Row>

                  {/* Bar Chart */}
                  {barChartData && analisaSkorData.length > 0 && (
                    <>
                      <h6 className="card-title mb-3 text-dark text-center">Perbandingan Skor Anda vs. Nilai Ambang Batas</h6>
                      <div className="chart-container" style={{ height: '350px' }}>
                          <Bar
                          data={barChartData}
                          options={{
                              maintainAspectRatio: false,
                              responsive: true,
                              scales: {
                              x: {
                                  grid: {
                                  display: false
                                  }
                              },
                              y: {
                                  beginAtZero: true,
                                  suggestedMax: Math.max(
                                    ...analisaSkorData.map(item => item.skor_anda),
                                    ...analisaSkorData.map(item => item.passing_grade_min || 0) 
                                  ) * 1.2,
                                  ticks: {
                                      stepSize: 50,
                                  }
                              }
                              },
                              plugins: {
                              legend: {
                                  position: 'top',
                              },
                              tooltip: {
                                  callbacks: {
                                  label: function(context) {
                                      let label = context.dataset.label || '';
                                      if (label) {
                                      label += ': ';
                                      }
                                      if (context.parsed.y !== null) {
                                      label += context.parsed.y;
                                      }
                                      return label;
                                  }
                                  }
                              }
                              }
                          }}
                          />
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* 3. Analisis Waktu Pengerjaan */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded">
                <CardBody className="p-3">
                  <h5 className="card-title mb-3 text-dark">Analisis Waktu Pengerjaan</h5>
                  <div className="mb-3">
                    <p className="font-size-14 text-muted mb-1">
                      Total Waktu Digunakan: <span className="fw-semibold text-dark">{resultData.time_taken_overall_display}</span> dari {resultData.duration_allowed_minutes} Menit
                    </p>
                    <Progress
                      value={timeUsedPercentage}
                      color={timeStatusColor}
                      className="mb-2"
                      style={{ height: '10px' }}
                    />
                    <small className="text-muted">
                        Efisiensi waktu: {timeUsedPercentage.toFixed(1)}%
                    </small>
                  </div>
                  <Row className="gy-2">
                      {analisaSkorData.length > 0 ? (
                          analisaSkorData.map((dataItem, index) => (
                            <Col xs={12} sm={4} key={`time-${index}`}>
                                <div className="p-2 border rounded bg-white font-size-13">
                                    <span className="text-muted d-block">{dataItem.kategori_soal.toUpperCase()}:</span>
                                    {/* Menggunakan formatDuration pada durasi_per_kategori_detik */}
                                    <span className="fw-semibold text-dark">{formatDuration(dataItem.durasi_per_kategori_detik)}</span>
                                </div>
                            </Col>
                          ))
                      ) : (
                        <Col xs={12} className="text-center text-muted">
                          <p>Tidak ada data durasi per bagian yang tersedia.</p>
                        </Col>
                      )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>


          {/* Tombol Pembahasan and Review Jawaban Button */}
          <Row className="mb-5">
            <Col className="text-center d-flex justify-content-center flex-wrap gap-3">
              <Button
                color="info" 
                className="btn-lg px-4 py-2 rounded-pill shadow"
                onClick={() => navigate(`/pembahasan?id_tryout_answer=${resultData.id_tryout_answer}`)}
              >
                Lihat Pembahasan <i className="mdi mdi-text-box-search-outline ms-2"></i>
              </Button>

            </Col>
          </Row>

        </Container>
      </div>

      {/* Custom Styles */}
      <style>
        {`
        .transition-300ms {
          transition: all 0.3s ease-in-out;
        }
        .hover-shadow:hover {
          box-shadow: 0 .15rem .3rem rgba(0,0,0,.05)!important;
        }
        .custom-alert-info-bg {
            background-color: #d1ecf1; /* Light blue */
            color: #0c5460; /* Darker blue */
        }
        .custom-alert-info-bg .alert-heading,
        .custom-alert-info-bg p {
            color: #0c5460;
        }
        .custom-alert-info-bg .mdi-information-outline {
            color: #0c5460;
        }
        .custom-section-card {
            background-color: #f8f9fa; /* Light grey background for section cards */
            border: 1px solid #e9ecef;
        }
        /* Re-using color classes from previous component for consistency */
        .bg-success-subtle { background-color: #d4edda !important; }
        .text-success { color: #155724 !important; }
        .bg-info-subtle { background-color: #d1ecf1 !important; }
        .text-info { color: #0c5460 !important; }
        .bg-warning-subtle { background-color: #fff3cd !important; }
        .text-warning { color: #856404 !important; }
        .bg-primary-subtle { background-color: #cce5ff !important; }
        .text-primary { color: #004085 !important; }
        .bg-secondary-subtle { background-color: #e2e3e5 !important; }
        .text-secondary { color: #383d41 !important; }
        .bg-danger-subtle { background-color: #f8d7da !important; }
        .text-danger { color: #721c24 !important; }
        .bg-dark-subtle { background-color: #d6d8d9 !important; }
        .text-dark { color: #1b1e21 !important; }

        /* Font Sizes */
        .font-size-12 { font-size: 0.75rem !important; }
        .font-size-13 { font-size: 0.8125rem !important; }
        .font-size-14 { font-size: 0.875rem !important; }
        .font-size-15 { font-size: 0.9375rem !important; }
        .font-size-16 { font-size: 1rem !important; }
        .font-size-20 { font-size: 1.25rem !important; }
        `}
      </style>
    </React.Fragment>
  );
};

export default TryOutResultDetail;